import React from 'react';
import { Outlet, useParams } from 'react-router-dom';

import { useEntityByIdSuspenseQuery } from '@eluve/apollo-client';
import {
  Button,
  Icon,
  LinkButton,
  SidebarTrigger,
  Stack,
  Text,
} from '@eluve/components';
import { ChatWithPatientData } from '@eluve/feature-chat-with-patient-data';
import { getPatientDetailsQuery } from '@eluve/frontend-appointment-hooks';
import { MedplumFhirRendererProvider } from '@eluve/medplum-client';
import { TenantOrUserFeatureFlaggedComponent } from '@eluve/smart-blocks';
import { formatHumanName } from '@eluve/utils';

import { useEnsureFhirPatient } from '../medplum/useEnsureFhirPatient';

import { ProfileTabNav } from './ProfileTabNav';

export const PatientProfilePage: React.FC<{ baseUrl: string }> = ({
  baseUrl,
}) => {
  const { tenantId, patientId } = useParams() as {
    tenantId: string;
    patientId: string;
  };
  const data = useEntityByIdSuspenseQuery(
    getPatientDetailsQuery,
    { tenantId, patientId },
    { entityKey: 'patientsByPk', entityType: 'Patient' },
  );
  const patient = data.patientsByPk;

  // Provision the FHIR Patient on demand so the FHIR-backed sections below
  // have a record to render even when no appointment has ever synced one.
  useEnsureFhirPatient(patientId);

  const name = formatHumanName(patient.firstName, patient.lastName);
  const initials = `${patient.firstName?.charAt(0) ?? ''}${patient.lastName?.charAt(0) ?? ''}`;

  return (
    <>
      <header className="col-span-2 row-start-1 flex h-12 shrink-0 items-center">
        <div className="flex w-76 shrink-0 items-center gap-1 pr-6 pl-2">
          <SidebarTrigger />
          <LinkButton
            to={`/organizations/${tenantId}/patients`}
            variant="ghost"
            size="sm"
          >
            <Icon name="ArrowLeft" size="auto" />
            All patients
          </LinkButton>
        </div>
        <div className="flex min-w-0 flex-1 items-center justify-between pr-2 pl-6">
          <ProfileTabNav />
          <TenantOrUserFeatureFlaggedComponent flag="INSIGHTS_CHAT">
            <ChatWithPatientData
              tenantId={tenantId}
              patientId={patientId}
              baseUrl={baseUrl}
              trigger={
                <Button variant="ghost" size="sm">
                  <Icon name="MessageSquare" size="auto" />
                  Ask Eluve
                </Button>
              }
            />
          </TenantOrUserFeatureFlaggedComponent>
        </div>
      </header>
      <MedplumFhirRendererProvider>
        <div className="col-start-1 row-start-2 flex min-h-0 overflow-hidden">
          <section className="w-76 shrink-0 scroll-fade overflow-y-auto p-6">
            <Stack
              direction="horizontal"
              gap={3}
              fullWidth={false}
              className="items-center"
            >
              <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-muted text-sm font-semibold privacy-text">
                {initials}
              </span>
              <Text size="md" weight="semibold" className="privacy-text">
                {name}
              </Text>
            </Stack>
            <Text color="tertiary" weight="regular" className="mt-6">
              Patient details will live here.
            </Text>
          </section>
          <section className="min-w-0 flex-1 scroll-fade overflow-y-auto p-6">
            <div className="max-w-5xl">
              <Outlet />
            </div>
          </section>
        </div>
      </MedplumFhirRendererProvider>
    </>
  );
};
